import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { TerritoryViewStore } from "../../store";
import { TerritoryView } from "../view/TerritoryView";
import { TerritoryUpdateStore } from "../../store";
import { TerritoryUpdate } from "../update/TerritoryUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type TerritoryModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const TerritoryModalContent: React.FC<TerritoryModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new TerritoryUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new TerritoryViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <TerritoryView
            territoryViewStore={storeInfo.store as TerritoryViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <TerritoryUpdate
            territoryUpdateStore={storeInfo.store as TerritoryUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
